import { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
} from "../../../../components";
import { Divider, Skeleton } from "@mui/material";
import { MonitorFinanceiroService } from "../../../../services";
import { formatPrice, responseErros } from "../../../../utils";

const monitorFinanceiroService = new MonitorFinanceiroService();

const COR_ENTRADA = "#388e3c";
const COR_SAIDA = "#d32f2f";
const defaultData = { totalEntradas: 0, totalSaidas: 0, categoriasEntrada: [] };

function LegendaItem({ cor, label, valor, pct }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb={0.75}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: cor,
          }}
        />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
          {formatPrice(valor)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", minWidth: 36, textAlign: "right" }}
        >
          {pct.toFixed(1)}%
        </Typography>
      </Box>
    </Box>
  );
}

function MonitorFinEntradasSaidasTotais({ filtrosBody }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultData);

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosBody]);

  async function buscar() {
    setLoading(true);
    const result =
      await monitorFinanceiroService.getDashboardEntradasSaidasTotais(
        filtrosBody,
      );
    if (!result.isAxiosError) {
      setData(result.data || defaultData);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const totalEntradas = data.totalEntradas || 0;
  const totalSaidas = data.totalSaidas || 0;
  const total = totalEntradas + totalSaidas;
  const pctEntradas = total ? (totalEntradas / total) * 100 : 0;
  const pctSaidas = total ? (totalSaidas / total) * 100 : 0;
  const categorias = data.categoriasEntrada || [];

  const chartOptions = {
    chart: {
      type: "pie",
      height: 170,
      backgroundColor: "transparent",
      margin: [4, 4, 4, 4],
    },
    title: { text: "" },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      backgroundColor: "#fff",
      borderWidth: 0,
      borderRadius: 4,
      pointFormatter: function () {
        return `<b>${this.name}</b>: ${formatPrice(
          this.y,
        )} (${this.percentage.toFixed(1)}%)`;
      },
    },
    plotOptions: {
      pie: {
        innerSize: "72%",
        size: "92%",
        center: ["50%", "50%"],
        borderWidth: 2,
        borderColor: "#fff",
        dataLabels: { enabled: false },
      },
    },
    series: [
      {
        name: "Total",
        animation: false,
        data: [
          { name: "Entradas", y: totalEntradas, color: COR_ENTRADA },
          { name: "Saídas", y: totalSaidas, color: COR_SAIDA },
        ],
      },
    ],
  };

  return (
    <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0", height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Composição de Entradas x Saídas
          </Typography>
          <i
            className="ph-bold ph-chart-pie-slice"
            style={{ color: "#64748b", fontSize: 20 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        ) : total === 0 ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography variant="body2" color="text.secondary">
              Sem movimentações no período selecionado.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <Box sx={{ position: "relative" }}>
                <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 13,
                      color: "#1e293b",
                      lineHeight: 1.1,
                    }}
                  >
                    {formatPrice(totalEntradas - totalSaidas)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Saldo
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 1 }}>
                <LegendaItem
                  cor={COR_ENTRADA}
                  label="Entradas"
                  valor={totalEntradas}
                  pct={pctEntradas}
                />
                <LegendaItem
                  cor={COR_SAIDA}
                  label="Saídas"
                  valor={totalSaidas}
                  pct={pctSaidas}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={7}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Maiores Categorias de Entrada
              </Typography>
              <Box sx={{ mt: 1 }}>
                {categorias.length === 0 ? (
                  <Typography variant="body2" color="text.disabled">
                    Sem categorias de entrada no período.
                  </Typography>
                ) : (
                  categorias.map((c, i) => (
                    <Box
                      key={i}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ py: 0.5 }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled", minWidth: 16 }}
                        >
                          {i + 1}.
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#1e293b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 180,
                          }}
                          title={c.categoria}
                        >
                          {c.categoria}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#1e293b" }}
                      >
                        {formatPrice(c.valor)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

export default MonitorFinEntradasSaidasTotais;
